import BreadCrumb from "Components/Common/BreadCrumb";
import PageTabs from "Components/Common/PageTabs";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import InvoiceSummary from "Components/Invoices/InvoiceSummary";
import InvoiceDescription from "Components/Invoices/InvoiceDescription";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  clearInvoiceDetails,
  clearInvoicePdfPreview,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
  selectInvoicesState,
} from "slices/invoices/reducer";
import {
  deleteInvoice,
  getInvoiceById,
  getInvoicePdfPreview,
} from "slices/invoices/thunk";
import Loader from "Components/Common/Loader";
import ShareModal from "Components/Modals/ShareModal";
import PdfPreviewModal from "Components/Modals/PdfPreviewModal";
import Icon, { IconName } from "Components/Icons/Icon";
import { ErrorHandler, ErrorToast } from "utils/toast";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { clearItemState } from "slices/items/reducer";
import {  useAppSelector } from "Components/Hooks/hooks";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import TableContainer from "Components/Common/TableContainer";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
// import { getPayments } from "slices/thunks";

const InvoiceDetails = () => {
  const [tab, setTab] = useState("1");
  const [showShareModal, setshowShareModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [deleteInvoiceModal, setDeleteInvoiceModal] = useState<boolean>(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const { state } = useLocation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const user = useAppSelector((state) => state.auth.user);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => { },
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner.",
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const paymentColumns = useMemo(
    () => [
      {
        header: "DATE",
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell.getValue()) ?? "-"}
          </span>
        ),
      },
      {
        header: "AMOUNT",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "SOURCE",
        accessorKey: "source",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ?? "-"}
          </span>
        ),
      },
      {
        header: "ALLOCATED",
        accessorKey: "allocated",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
    ],
    [],
  );

  const handleCreateRecord = () => {
    navigate("/record-payment", {
      state: {
        invoice_id: state?.id,
        type: "Invoice",
      },
    });
  };

  const quoteSelector = createSelector(selectInvoicesState, (state) => ({
    invoiceDetails: state.invoiceDetails,
    isLoading: state.status.fetchById,
    error: state.errors.fetchById,
    pdfPreviewHtml: state.pdfPreviewHtml,
  }));

  const { invoiceDetails, isLoading, error, pdfPreviewHtml } =
    useSelector(quoteSelector);

  const openShareModal = () => setshowShareModal(true);
  const closeShareModal = () => setshowShareModal(false);

  const closePdfPreviewModal = () => {
    setShowPdfPreviewModal(false);
    dispatch(clearInvoicePdfPreview());
  };

  const moreActionGroups = [
    invoiceDetails?.is_editable
      ? [
        {
          label: "Edit",
          icon: <Icon name="Edit" />,
          onClick: () => {
            if (hasNotSubscribed) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(
                RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
              );
            } else if (!hasBankAccount && !hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(
                RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
              );
            } else if (!hasBankAccount) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(
                RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
              );
            } else if (!hasSignature) {
              setShowSubscriptionExpiredPopUp(true);
              setRestrictionModalData(RESTRICTION_CONFIG.SIGNATURE_REQUIRED);
            } else {
              dispatch(clearQuoteDetails());
              dispatch(clearInvoiceDetails());
              dispatch(clearItemState());
              dispatch(resetEditInvoiceStatus());
              dispatch(resetCreateInvoiceStatus());
              navigate("/manage-invoice", {
                state: { id: invoiceDetails.id },
              });
            }
          },
        },
        {
          label: "Delete",
          icon: <Icon name="Delete" />,
          onClick: () => {
            setDeleteInvoiceModal(true);
          },
        },
      ]
      : [
        {
          label: "Delete",
          icon: <Icon name="Delete" />,
          onClick: () => {
            setDeleteInvoiceModal(true);
          },
        },
      ],
  ];

  const handleDownload = async () => {
    try {
      setIsLoadingDownload(true);

      // Create URL for the blob
      const url = `${invoiceDetails?.url}?download=1`;
      // Create link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceDetails?.invoice_number}.pdf`;
      link.click();

      // Cleanup
      URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const handlePreview = async () => {
    if (!invoiceDetails) return;
    try {
      setIsLoadingPreview(true);
      await dispatch(getInvoicePdfPreview(invoiceDetails.id)).unwrap();
      setShowPdfPreviewModal(true);
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const shareExportsGroups = [
    [
      {
        label: "Download",
        icon: <Icon name="Download" />,
        onClick: handleDownload,
      },
      {
        label: "Share",
        icon: <Icon name="BlackShare" />,
        onClick: openShareModal,
      },
    ],
  ];

  useEffect(() => {
    if (state?.id) {
      dispatch(getInvoiceById(state.id));
    } else {
      navigate(-1);
    }
  }, [dispatch, state?.id, navigate]);

  if (isLoading === "loading" || !invoiceDetails) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Invoice Detail"
            pageTitle="Invoices"
            url="/invoices"
          />
          <div className="gap-4 d-flex flex-column">
            <PageHeaderActions
              title={invoiceDetails.quote.title}
              subtitle={invoiceDetails.invoice_number}
              actions={[
                {
                  label: "Preview",
                  onClick: handlePreview,
                  leftIcon: <Icon name="Pause" />,
                  isLoading: isLoadingPreview,
                },
                {
                  type: "dropdown",
                  label: "More Actions",
                  groups: moreActionGroups,
                },
                {
                  type: "dropdown",
                  label: "Share & Export",
                  groups: shareExportsGroups,
                  isLoading: isLoadingDownload,
                },
              ]}
            />
            <Col xxl={12}>
              <PageTabs
                active={tab}
                onChange={setTab}
                tabs={[
                  { id: "1", label: "Summary" },
                  { id: "2", label: "Description" },
                  { id: "3", label: "Payments" },
                ]}
              />
              {tab === "1" && <InvoiceSummary invoice={invoiceDetails} />}
              {tab === "2" && <InvoiceDescription invoice={invoiceDetails} />}
              {tab === "3" && (
                <Row>
                  <Col xxl={12}>
                    <Card className="br-10 bg-white">
                      <CardBody className="pt-0">
                        <TableContainer
                          columns={paymentColumns}
                          data={invoiceDetails?.payments || []}
                          isGlobalFilter={false}
                          divClass="table-responsive table-card mb-2 bg-white"
                          tableClass="align-middle table-nowrap table-borderless"
                          thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                          theadClass="table-light"
                          showPagination={false}
                          isPaymentDetails
                          onCreatePaymentRecordClick={handleCreateRecord}
                          isShowRecordPaymentButton={
                            invoiceDetails?.status !== "paid"
                          }
                        />

                        {(invoiceDetails.payment_method) === "cash" && (
                          <>
                            <div className="dashed mx-n4 mt-0 pt-0" />
                            <div className="d-flex gap-2 mt-2 position-relative">
                              <Icon name='InfoOrange' width={16} height={16} className="align-self-start position-absolute" />
                              <span className="fs-14 text-orangeFF fw-normal text-start ms-4" >
                                Cash or offline payments should be completed directly and are not processed online.
                              </span>
                            </div>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </Col>
          </div>
        </Container>
      </div>
      <ShareModal
        isOpen={showShareModal}
        onClose={closeShareModal}
        pdfTitle={invoiceDetails?.invoice_number}
        pdfUrl={invoiceDetails?.url}
        route_url={invoiceDetails?.route_url}
        isInvoice={true}
        invoiceDetails={invoiceDetails}
      />
      <PdfPreviewModal
        isOpen={showPdfPreviewModal}
        onClose={closePdfPreviewModal}
        html={pdfPreviewHtml || ""}
      />

      <DeleteItemModal
        show={deleteInvoiceModal}
        onDeleteClick={() => {
          dispatch(deleteInvoice(invoiceDetails.id));
          setDeleteInvoiceModal(false);
          navigate(-1);
        }}
        onCloseClick={() => {
          setDeleteInvoiceModal(false);
        }}
      />
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />
    </>
  );
};

export default InvoiceDetails;
